"use client";

import Link from "next/link";
import { MouseEvent, MouseEventHandler, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { isEmpty } from "~/utils/isEmpty";
import { GtmEvent } from "~/lib/gtm/getGtmEventData";
import { useTrackEvent } from "~/hooks/useTrackEvent";

import Overline from "~/componentsDS/Typography/Overline";

const badgeVariants = cva("rounded-full inline-block justify-center items-center disabled:opacity-40 disabled:text-pureWhite transition", {
    variants: {
        variant: {
            "primary": "text-pureWhite bg-primary300 hover:bg-primary400 focus:bg-primary500 border border-transparent",
            "primaryLight": "text-primary500 bg-[#D7E1FF] hover:bg-primary200 focus:bg-primary200 border border-transparent",
            "secondary": "text-primary500 bg-primary200 hover:bg-primary300 hover:text-pureWhite focus:bg-primary300 focus:text-pureWhite border border-transparent focus:border-trueBlack/10",
            secondaryTextBlue:
            "text-primary500 bg-[#D7E1FF] border border-transparent rounded-full " +
            "hover:bg-primary300 hover:text-pureWhite hover:border-transparent " +
            "active:bg-primary400 active:text-pureWhite active:border-trueBlack/10 " +
            "focus:bg-primary400 focus:text-pureWhite focus:border-trueBlack/10",
            "tertiary": "text-primary300 bg-transparent hover:bg-primary100 focus:bg-primary100 border-2 border-primary300",
            "outlineSubtle": "text-primary400 bg-transparent hover:bg-gray100 border border-gray200 focus:text-primary500 focus:border-primary500 focus:bg-primary200",
            "danger": "text-pureWhite bg-error500 hover:bg-error500/90 focus:bg-error500/90 border border-transparent",
            "ghost": "text-primary500 bg-transparent hover:bg-primary100 focus:bg-primary200 border border-transparent focus:border-trueBlack/10",
            "primaryOverlay": "text-pureWhite bg-transparent hover:bg-pureWhite/25 hover:backdrop-blur-[10px] focus:bg-pureWhite/25 border-2 border-pureWhite focus:border-pureWhite/50",
            "secondaryOverlay": "text-pureWhite bg-pureWhite/25 backdrop-blur-[10px] hover:bg-pureWhite/25 focus:bg-pureWhite/25",
            "tertiaryOverlay": "text-pureWhite bg-trueBlack/50 backdrop-blur-[10px] hover:bg-trueBlack/60 focus:bg-trueBlack/60",
            "white": "text-primary500 bg-pureWhite hover:bg-primary200 focus:bg-primary300 border border-transparent",
        },
        size: {
            "md": "py-[18px] px-8",
            "sm": "pt-[12px] pb-[10px] px-6",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "md",
    },
});

type Props = VariantProps<typeof badgeVariants> & {
    children?: ReactNode;
    className?: string;
    href?: string;
    rel?: string;
    eventData?: GtmEvent["value"];
    onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    target?: "_blank" | "_top";
    disabled?: boolean;
    /** `disabled` is not valid on `<a>`, so link CTAs that hold a click use this instead. */
    "aria-disabled"?: boolean;
    /** For toggle buttons, such as the options of a single-select filter. */
    "aria-pressed"?: boolean;
};

const isModifiedClick = (event: MouseEvent) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

// A malformed escape like "#%" throws in decodeURIComponent; use the raw id then.
const decodeHashId = (hash: string) => {
    try {
        return decodeURIComponent(hash.slice(1));
    } catch {
        return hash.slice(1);
    }
};

// A native anchor jump adds a history entry with null state, which Next's popstate handler ignores, so Back changes the URL but not the page.
// Next's patched pushState copies its router state onto the new entry.
const scrollToHash = (event: MouseEvent, hash: string) => {
    event.preventDefault();
    if (new URL(hash, window.location.href).href !== window.location.href) {
        window.history.pushState(null, "", hash);
    }
    const id = decodeHashId(hash);
    if (id) {
        document.getElementById(id)?.scrollIntoView();
    } else {
        window.scrollTo(0, 0);
    }
};

export const Button = ({ children, className, variant, size, eventData, onClick, ...props }: Props) => {
    const { href = "" } = props;
    const isExternal = href.startsWith("http");
    // Next's Link ignores a click whose hash matches the current URL, so only the first "#" click would scroll.
    const isHashLink = href.startsWith("#");

    const sendEvent = useTrackEvent();

    const handleOnClick: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = (event) => {
        if (onClick && typeof onClick === "function") {
            onClick(event);
        }

        if (isHashLink && !props.target && !event.defaultPrevented && !isModifiedClick(event)) {
            scrollToHash(event, href);
        }

        if (eventData && !isEmpty(eventData)) {
            const url = isExternal && new URL(href);
            const isThirdpartyLink = isExternal && url && !url.hostname.includes("innerbalance.com");
            sendEvent({
                event: "user_interaction",
                value: { ...eventData, href: isThirdpartyLink || !url ? href : url.pathname },
            });
        }
    };

    const classNames = cn(badgeVariants({ className, variant, size }));

    const child = (
        <Overline as="span" size={size} weight="500" className={cn("flex justify-center items-center", size === "sm" ? "gap-2" : "gap-3")}>
            {children}
            {props.target === "_blank" && <span className="sr-only"> (opens in a new tab)</span>}
        </Overline>
    );

    if (href) {
        if (isExternal || isHashLink) {
            return <a {...props} href={href} onClick={handleOnClick} className={classNames}>{child}</a>;
        } else {
            return <Link {...props} href={href} onClick={handleOnClick} className={classNames} prefetch={true}>{child}</Link>;
        }
    }

    return (
        <button {...props} onClick={handleOnClick} className={classNames}>{child}</button>
    );
};

export default Button;
